import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";

/**
 * GenericAnalysisResult
 * ─────────────────────
 * Renders the generic `col1..col7` table returned by most graph-analysis
 * levels. Column headers and per-cell formatters are driven by the `mode`
 * prop so the same component handles every level that produces a table.
 *
 * Props
 *   mode        String — level key ('level4','level5','level6','level7',
 *               'level9','level12','communities'). Controls column headers
 *               and cell formatting.
 *   tableData   Array — the raw `tableData` returned by the backend.
 *   emptyText   Optional string to show when no rows are present.
 *   highlight   Optional color accent for the main sortable column.
 */
const GenericAnalysisResult = ({
    mode,
    tableData,
    emptyText = "No results found.",
    highlight = "primary",
}) => {
    if (!Array.isArray(tableData) || tableData.length === 0) {
        return (
            <Message
                severity="info"
                className="w-full"
                text={emptyText}
            />
        );
    }

    const schema = SCHEMAS[mode] || DEFAULT_SCHEMA;

    return (
        <DataTable
            value={tableData}
            paginator
            rows={10}
            stripedRows
            showGridlines
            size="small"
            emptyMessage={emptyText}
        >
            {schema.columns.map((c) => (
                <Column
                    key={c.field}
                    field={c.field}
                    header={c.header}
                    sortable={c.sortable !== false}
                    body={c.body}
                    style={c.style}
                />
            ))}
        </DataTable>
    );
};

// ─── Per-level schemas ──────────────────────────────────────────────────

const residueCol = (field = "col1", header = "Residue") => ({
    field,
    header,
    body: (row) => <span className="fw-bold text-primary">{row[field]}</span>,
});

const numericCol = (field, header, decimals = 2) => ({
    field,
    header,
    body: (row) => {
        const v = row[field];
        if (v == null || v === "-") return "-";
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(decimals) : String(v);
    },
});

const yesNoCol = (field, header, yesSeverity = "warning", yesIcon = "pi pi-check") => ({
    field,
    header,
    body: (row) =>
        row[field] === "Yes" ? (
            <Tag value="Yes" severity={yesSeverity} icon={yesIcon} rounded />
        ) : (
            <Tag value="No" severity="secondary" rounded />
        ),
});

const pathogenicityTag = (text) => {
    if (!text) return <span className="text-secondary">-</span>;
    const s = String(text).toLowerCase();
    if (s.includes("pathogenic") && !s.includes("likely_benign"))
        return <Tag value={text} severity="danger" icon="pi pi-exclamation-triangle" />;
    if (s.includes("benign"))
        return <Tag value={text} severity="success" />;
    if (s.includes("uncertain") || s.includes("vus"))
        return <Tag value={text} severity="warning" />;
    return <span>{text}</span>;
};

const DEFAULT_SCHEMA = {
    columns: [
        residueCol("col1", "Field 1"),
        { field: "col2", header: "Field 2" },
        { field: "col3", header: "Field 3" },
        { field: "col4", header: "Field 4" },
        { field: "col5", header: "Field 5" },
        { field: "col6", header: "Field 6" },
    ],
};

const SCHEMAS = {
    // ── level4: Ligand–Variant Overlap ────────────────────────────────
    level4: {
        columns: [
            { field: "col1", header: "Ligand",
              body: (r) => <span className="fw-bold text-primary">{r.col1}</span> },
            { field: "col2", header: "Residue",
              body: (r) => <span className="fw-bold">{r.col2}</span> },
            { field: "col3", header: "Variant",
              body: (r) => pathogenicityTag(r.col3) },
            { field: "col4", header: "AlphaMissense",
              body: (r) => r.col4
                ? <Tag value={r.col4} severity="info" rounded />
                : <span className="text-secondary">-</span> },
            { field: "col5", header: "Mechanism" },
            { field: "col6", header: "Source" },
        ],
    },

    // ── level5: Topological anomalies ────────────────────────────────
    level5: {
        columns: [
            residueCol("col1", "Residue"),
            { field: "col2", header: "Degree" },
            { field: "col3", header: "k-core",
              body: (r) => <Tag value={r.col3} severity="info" rounded /> },
            yesNoCol("col4", "Articulation", "danger", "pi pi-exclamation-circle"),
            { field: "col5", header: "Scope" },
        ],
    },

    // ── level6: PTM Crosstalk ────────────────────────────────────────
    level6: {
        columns: [
            { field: "col1", header: "PTM 1",
              body: (r) => <span className="fw-bold text-primary">{r.col1}</span> },
            { field: "col2", header: "PTM 2",
              body: (r) => <span className="fw-bold text-primary">{r.col2}</span> },
            numericCol("col3", "Distance (Å)", 2),
            { field: "col4", header: "Source",
              body: (r) => <Tag value={r.col4} severity="info" /> },
        ],
    },

    // ── level7: PTM Hotspots ─────────────────────────────────────────
    level7: {
        columns: [
            residueCol("col1", "Center residue"),
            { field: "col2", header: "PTMs / window" },
            { field: "col3", header: "Enrichment",
              body: (r) => <Tag value={r.col3} severity="warning" icon="pi pi-chart-line" /> },
            { field: "col4", header: "PTM types" },
        ],
    },

    // ── level9: PTM–Variant Mechanism ────────────────────────────────
    level9: {
        columns: [
            { field: "col1", header: "Variant",
              body: (r) => pathogenicityTag(r.col1) },
            { field: "col2", header: "PTM partner",
              body: (r) => <span className="fw-bold">{r.col2}</span> },
            numericCol("col3", "Distance (Å)", 2),
            { field: "col4", header: "Mechanism",
              body: (r) => <Tag value={r.col4}
                severity={r.col4 === "Direct PTM Abolition" ? "danger" : "warning"} /> },
            { field: "col5", header: "Source" },
        ],
    },

    // ── level12: VUS Resolver ────────────────────────────────────────
    level12: {
        columns: [
            { field: "col1", header: "Target",
              body: (r) => <span className="fw-bold">{r.col1}</span> },
            { field: "col2", header: "Neighbor",
              body: (r) => <span className="fw-bold text-primary">{r.col2}</span> },
            { field: "col3", header: "Context",
              body: (r) => {
                  const sev = r.col3 === "Pathogenic Neighbor" ? "danger"
                      : r.col3 === "Ligand Binding" ? "info"
                      : r.col3 === "Functional Site" ? "success"
                      : r.col3 === "Domain Core" ? "warning"
                      : "secondary";
                  return <Tag value={r.col3} severity={sev} />;
              } },
            { field: "col4", header: "Features",
              body: (r) => <span className="small">{r.col4}</span> },
            numericCol("col5", "Distance (Å)", 2),
        ],
    },

    // ── communities: Louvain modules ─────────────────────────────────
    communities: {
        columns: [
            { field: "col1", header: "Module",
              body: (r) => <Tag value={r.col1} severity="info" rounded /> },
            { field: "col2", header: "Size" },
            { field: "col3", header: "Members preview",
              body: (r) => <span className="small font-mono">{r.col3}</span> },
            { field: "col4", header: "Scope" },
        ],
    },
};

export default GenericAnalysisResult;
